
import React, { useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { AudioWaveform, Volume2, Sparkles } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface AINoiseControlProps {
  isEnabled: boolean;
  onToggleNoiseReduction: (enabled: boolean) => void;
  reductionLevel: number;
  onReductionLevelChange: (level: number) => void;
}

const AINoiseControl: React.FC<AINoiseControlProps> = ({
  isEnabled,
  onToggleNoiseReduction,
  reductionLevel,
  onReductionLevelChange
}) => {
  const { toast } = useToast();
  const [isOptimizing, setIsOptimizing] = useState(false);
  
  const handleOptimize = () => {
    setIsOptimizing(true);
    
    setTimeout(() => {
      setIsOptimizing(false);
      onReductionLevelChange(85);
      
      toast({
        title: "AI Optimization Complete",
        description: "Noise reduction settings have been automatically optimized based on your environment."
      });
    }, 2000);
  };
  
  return (
    <div className="space-y-4 p-4 bg-black/20 rounded-lg border border-white/10">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-400" />
          <h3 className="text-sm font-medium text-white">AI Noise Reduction</h3>
        </div>
        <Switch 
          checked={isEnabled}
          onCheckedChange={onToggleNoiseReduction}
        />
      </div>
      
      {isEnabled && (
        <>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/70">Reduction Level</span>
              <span className="text-xs text-white/70">{reductionLevel}%</span>
            </div>
            <Slider
              value={[reductionLevel]}
              min={0}
              max={100}
              step={5}
              onValueChange={(value) => onReductionLevelChange(value[0])}
              disabled={isOptimizing}
            />
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full text-xs gap-1"
              onClick={handleOptimize}
              disabled={isOptimizing}
            >
              {isOptimizing ? (
                <>
                  <Volume2 className="h-3 w-3 animate-pulse" />
                  Analyzing...
                </>
              ) : (
                <>
                  <AudioWaveform className="h-3 w-3" />
                  Auto-Optimize
                </>
              )}
            </Button>
          </div>
          
          <div className="text-xs text-white/50 italic">
            Reduces background noise, keyboard sounds, room echo, and other unwanted audio.
          </div>
        </>
      )}
    </div>
  );
};

export default AINoiseControl;
